import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calendar, Clock, MapPin, ChevronLeft, Users } from "lucide-react";
import { api, openVaycrayCheckout } from "../lib/api";
import { BackgroundTexture } from "../components/BackgroundTexture";
import { Footer } from "../components/Footer";

const VENUE_LAYOUT_URL = "https://customer-assets.emergentagent.com/job_27178513-f6fb-45e5-994a-7b52c1854711/artifacts/r1r7sj64_argy%20table%20layout%20%282%29.png";

const TABLE_ACCENTS = ["#E8915A", "#D4A574", "#C68B6A", "#B87A5E", "#A0694F", "#8B5E44"];

export default function EventDetailPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([api.getEvent(slug), api.getSettings()])
      .then(([e, s]) => { setEvent(e.data); setSettings(s.data); })
      .catch(console.error);
  }, [slug]);

  if (!event) return (
    <div className="min-h-screen flex items-center justify-center content-layer">
      <div className="w-6 h-6 border-2 border-[#2D1810] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const hasTables = event.tables && event.tables.length > 0;
  const hasTableBooking = !!event.vaycray_table_slug;

  return (
    <div data-testid="event-detail-page" className="min-h-screen content-layer">
      <BackgroundTexture />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 lg:py-10 relative z-10">
        <Link to="/events" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6 transition-colors">
          <ChevronLeft size={16} /> Back to Events
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="aspect-[4/5] overflow-hidden rounded-md">
            <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
          </div>

          <div className="lg:py-4">
            <p className="text-sm text-white/60 font-medium mb-3">{event.date} &middot; {event.time}</p>
            <h1 className="font-syne text-3xl lg:text-4xl font-semibold text-white mb-4">{event.title}</h1>
            {event.tagline && <p className="text-lg text-white/70 mb-5">{event.tagline}</p>}
            <p className="text-white/70 leading-relaxed mb-8 whitespace-pre-wrap">{event.description}</p>

            <div className="panel-light rounded-md p-5 mb-8 space-y-3">
              <div className="flex items-center gap-3">
                <Calendar size={18} className="text-[#A04D1F]" />
                <span className="text-sm text-[#2D1810]">{event.date}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={18} className="text-[#A04D1F]" />
                <span className="text-sm text-[#2D1810]">{event.time}</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={18} className="text-[#A04D1F]" />
                <span className="text-sm text-[#2D1810]">RROV Goa</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {event.vaycray_slug ? (
                <button
                  onClick={() => openVaycrayCheckout(event.vaycray_slug)}
                  data-testid="buy-tickets-btn"
                  className="btn-dark px-8 py-4 text-sm font-medium text-center rounded-md inline-flex items-center justify-center gap-2"
                >
                  Buy Tickets <ArrowRight size={14} />
                </button>
              ) : event.ticket_url ? (
                <a
                  href={event.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buy-tickets-btn"
                  className="btn-dark px-8 py-4 text-sm font-medium text-center rounded-md inline-flex items-center justify-center gap-2"
                >
                  Buy Tickets <ArrowRight size={14} />
                </a>
              ) : null}
              {hasTables ? (
                <a href="#tables" className="btn-light px-8 py-4 text-sm font-medium text-center rounded-md">
                  Book VIP Table
                </a>
              ) : (
                <Link to="/vip" className="btn-light px-8 py-4 text-sm font-medium text-center rounded-md">
                  Book VIP Table
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Ticket Checkout Sections */}
        {event.vaycray_slug && (
          <div className="mt-16 pt-16 border-t border-white/20 text-center">
            <h2 className="font-syne text-2xl font-semibold text-white mb-4">Get Tickets</h2>
            <p className="text-white/60 text-sm mb-6">Secure your spot — powered by Vaycray.</p>
            <button
              onClick={() => openVaycrayCheckout(event.vaycray_slug)}
              data-testid="buy-tickets-section-btn"
              className="btn-dark px-10 py-4 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2"
            >
              Buy Tickets <ArrowRight size={14} />
            </button>
          </div>
        )}

        {!event.vaycray_slug && event.ticket_url && (
          <div className="mt-16 pt-16 border-t border-white/20 text-center">
            <h2 className="font-syne text-2xl font-semibold text-white mb-4">Get Tickets</h2>
            <p className="text-white/60 text-sm mb-6">Secure your spot for this event.</p>
            <a
              href={event.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="buy-tickets-section-btn"
              className="btn-dark px-10 py-4 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2"
            >
              Buy Tickets <ArrowRight size={14} />
            </a>
          </div>
        )}

        {/* VIP Table Section */}
        <div id="tables" className="mt-16 pt-16 border-t border-white/20">
          <div className="text-center mb-6">
            <p className="text-[11px] font-medium tracking-[0.15em] text-white/60 uppercase mb-2">VIP Experience</p>
            <h2 className="font-syne text-2xl lg:text-3xl font-semibold text-white">Book a VIP Table</h2>
          </div>

          <div className="max-w-4xl mx-auto overflow-hidden rounded-md mb-10">
            <img src={VENUE_LAYOUT_URL} alt="RROV Venue Table Layout" className="w-full h-auto object-contain" />
          </div>

          {hasTables ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
              {event.tables.map((table, idx) => (
                <div key={idx} data-testid={`table-card-${idx}`} className="panel-light rounded-md overflow-hidden">
                  <div className="h-1.5" style={{ backgroundColor: TABLE_ACCENTS[idx % TABLE_ACCENTS.length] }} />
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-syne text-lg font-semibold text-[#2D1810]">{table.category}</h3>
                      {table.price && (
                        <span className="text-sm font-semibold text-[#A04D1F]">{table.price}</span>
                      )}
                    </div>
                    <p className="text-xs text-[#2D1810]/40 font-medium uppercase tracking-wider mb-1">Tables: {table.tables_range}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#2D1810]/50 mb-4">
                      <Users size={12} />
                      <span>{table.capacity}</span>
                    </div>
                    {hasTableBooking ? (
                      <button
                        onClick={() => openVaycrayCheckout(event.vaycray_table_slug)}
                        data-testid={`select-table-btn-${idx}`}
                        className="btn-dark px-6 py-3 text-xs font-medium rounded-md inline-flex items-center justify-center gap-2 w-full"
                      >
                        Select Table <ArrowRight size={12} />
                      </button>
                    ) : (
                      <Link
                        to="/contact"
                        data-testid={`select-table-btn-${idx}`}
                        className="btn-dark px-6 py-3 text-xs font-medium rounded-md inline-flex items-center justify-center gap-2 w-full"
                      >
                        Enquire Now <ArrowRight size={12} />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center mb-8">
              <p className="text-white/60 text-sm mb-4">Premium tables with dedicated service and the best views in the house.</p>
              <Link
                to="/vip"
                data-testid="event-vip-link"
                className="btn-dark px-8 py-4 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2"
              >
                View VIP Options <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </div>
      <Footer settings={settings} />
    </div>
  );
}
